import * as React from 'react';
import { useMemo } from 'react';

import { FlexAligner, Tooltip, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { colors, responsive } from '@/utils';

// regex to match non-printable control characters (ASCII 0-31 and 127)
// oxlint-disable-next-line no-control-regex
const controlCharsRegex = /[\u0000-\u001F\u007F]/g;

type KeyValueRowProps = {
  /** Renders a space-between row: nowrap label + value. */
  label?: string;
  /** Shown when `value` is empty after trim. Defaults to translated `notApplicable` in the default (`common`) namespace. */
  alternativeText?: string | number;

  /** When `label` is set, applies to both label and value `Typography`; otherwise only the value. */
  disabled?: boolean;
  value: string | number | undefined;
  /** Font weight of the value `Typography`; default is 300. */
  weight?: number;
  tooltip?: string;
  unit?: string;
  /** If true, inserts a space between the value and `unit`. Default is false. */
  withSpaceToUnit?: boolean;
};

/**
 * Read-only value with optional unit and tooltip. With `label`, renders a key/value row (`FlexAligner` space-between).
 * The value column uses full width with ellipsis; value text is trimmed for control characters and whitespace.
 * `weight` must match a supported `Typography` weight.
 *
 * @param label When set, renders a nowrap label plus the value row; when omitted, only the value is rendered.
 * @param alternativeText Shown when `value` is empty after trim. Defaults to translated `notApplicable` from `common`.
 * @param disabled Disables the value `Typography`, and the label `Typography` when `label` is set.
 * @param value String or number to display; if empty, `alternativeText` or the default translated `notApplicable` is shown.
 * @param weight Font weight of the value `Typography`; default is 300.
 * @param tooltip Optional tooltip on the value (hover).
 * @param unit Optional suffix after the value, e.g. `'m'`, `'kg'`. Appended only when there is a non-empty trimmed value.
 * @param withSpaceToUnit If true, inserts a space between the value and `unit`. Default is false.
 */
export const KeyValueRow = ({
  label,
  alternativeText,
  disabled = false,
  value,
  weight = 300,
  tooltip = undefined,
  unit = undefined,
  withSpaceToUnit = false,
}: KeyValueRowProps) => {
  const { t } = useTranslation();
  const resolvedAlternativeText = alternativeText ?? t('notApplicable');

  const text = useMemo(() => {
    let trimmedValue: string | undefined;
    if (value === undefined || value === null) {
      trimmedValue = undefined;
    } else {
      const cleaned = value.toString().replace(controlCharsRegex, '').trim();
      trimmedValue = cleaned.length > 0 ? cleaned : undefined;
    }
    const valueString = trimmedValue ? trimmedValue : resolvedAlternativeText;
    const space = trimmedValue && withSpaceToUnit ? ' ' : '';
    const unitOrEmpty =
      trimmedValue && trimmedValue.length > 0 && valueString && unit ? `${unit}` : '';
    return `${valueString}${space}${unitOrEmpty}`;
  }, [value, resolvedAlternativeText, unit, withSpaceToUnit]);

  const valueEl = (
    <div style={{ overflow: 'hidden' }}>
      <Tooltip tooltip={tooltip} placement='top'>
        <Typography
          disabled={disabled}
          type='body_large'
          weight={weight}
          style={
            {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            } as React.CSSProperties
          }
        >
          {text}
        </Typography>
      </Tooltip>
    </div>
  );

  if (label !== undefined) {
    return (
      <FlexAligner
        spaceBetween
        alignment='center'
        gap={`${responsive.spacing['2xlarge']}`}
        style={{
          backgroundColor: colors.surface.sunken,
          padding: responsive.spacing.medium,
          borderRadius: responsive.radius.medium,
        }}
      >
        <Typography type='body_large' disabled={disabled} style={{ whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        {valueEl}
      </FlexAligner>
    );
  }

  return valueEl;
};
